import type { ReactNode } from 'react';
import type { DashboardGranularity } from '../../api/dashboard';
import { ViewportTooltip } from '../ui/HintTooltip';

type DashboardCardProps = {
  title: ReactNode;
  subtitle?: string;
  legend?: ReactNode;
  actions?: ReactNode;
  /** Крайний правый угол шапки (фрейм День/Час). */
  trailing?: ReactNode;
  children: ReactNode;
  className?: string;
};

export function DashboardCard({
  title,
  subtitle,
  legend,
  actions,
  trailing,
  children,
  className = '',
}: DashboardCardProps) {
  return (
    <section
      className={`flex min-w-0 flex-col overflow-visible rounded-2xl border border-slate-200 bg-white shadow-sm dark:border-gray-700 dark:bg-gray-800 ${className}`}
    >
      <div className="flex h-12 shrink-0 items-center gap-2 overflow-visible border-b border-slate-200 px-4 dark:border-gray-700 sm:gap-3 sm:px-5">
        <div className="min-w-0 shrink-0">
          {typeof title === 'string' ? (
            <h3 className="!mb-0 truncate !text-base !font-bold !leading-none !text-slate-800 dark:!text-gray-100">{title}</h3>
          ) : (
            title
          )}
          {subtitle ? <p className="mt-0.5 text-xs leading-none text-slate-500">{subtitle}</p> : null}
        </div>
        <div className="flex min-w-0 flex-1 items-center">{legend ?? null}</div>
        {actions ? <div className="flex h-6 shrink-0 items-center gap-1.5">{actions}</div> : null}
        {trailing ? <div className="ml-auto flex h-6 shrink-0 items-center pl-1">{trailing}</div> : null}
      </div>
      <div className="overflow-visible px-4 py-3 sm:px-5 sm:py-3.5">{children}</div>
    </section>
  );
}

type KpiCardProps = {
  label: string;
  value?: string;
  fullValue?: string;
  values?: Array<{ label: string; value: string; fullValue: string }>;
  hint?: string;
  tone?: 'rose' | 'emerald' | 'sky' | 'violet' | 'amber' | 'slate';
};

const toneClasses: Record<NonNullable<KpiCardProps['tone']>, string> = {
  rose: 'border-rose-200 bg-rose-50/80 text-rose-800',
  emerald: 'border-emerald-200 bg-emerald-50/80 text-emerald-800',
  sky: 'border-sky-200 bg-sky-50/80 text-sky-800',
  violet: 'border-violet-200 bg-violet-50/80 text-violet-800',
  amber: 'border-amber-200 bg-amber-50/80 text-amber-800',
  slate: 'border-slate-200 bg-slate-50 text-slate-800',
};

function KpiValue({
  value,
  fullValue,
  label,
}: {
  value: string;
  fullValue?: string;
  label?: string;
}) {
  const inner = (
    <>
      {label ? <div className="truncate text-[10px] font-medium opacity-60">{label}</div> : null}
      <div className="truncate font-mono text-lg font-bold leading-tight tabular-nums sm:text-xl">
        {value}
      </div>
    </>
  );
  if (fullValue && fullValue !== value) {
    return (
      <ViewportTooltip
        className="min-w-0"
        content={fullValue}
        contentClassName="whitespace-nowrap rounded-md bg-slate-900 px-2.5 py-1.5 text-[11px] font-medium normal-case tracking-normal text-white shadow-xl"
      >
        <div className="min-w-0">{inner}</div>
      </ViewportTooltip>
    );
  }
  return <div className="min-w-0">{inner}</div>;
}

export function KpiCard({
  label,
  value,
  fullValue,
  values,
  hint,
  tone = 'slate',
}: KpiCardProps) {
  return (
    <div className={`min-w-0 rounded-xl border px-3 py-2.5 shadow-sm ${toneClasses[tone]}`}>
      <div className="text-[11px] font-semibold uppercase tracking-[0.08em] opacity-70">{label}</div>
      <div className={`mt-1.5 ${values ? 'grid grid-cols-2 gap-2' : ''}`}>
        {values
          ? values.map((item) => (
              <KpiValue
                key={item.label}
                label={item.label}
                value={item.value}
                fullValue={item.fullValue}
              />
            ))
          : value != null
            ? <KpiValue value={value} fullValue={fullValue} />
            : null}
      </div>
      {hint ? <div className="mt-1 text-xs opacity-65">{hint}</div> : null}
    </div>
  );
}

export function SegmentedControl<T extends string>({
  value,
  options,
  onChange,
}: {
  value: T;
  options: Array<{ value: T; label: string }>;
  onChange: (value: T) => void;
}) {
  return (
    <div className="inline-flex h-6 items-center rounded-md border border-slate-200 bg-slate-100 p-px">
      {options.map((option) => (
        <button
          key={option.value}
          type="button"
          onClick={() => onChange(option.value)}
          className={`inline-flex h-5 items-center rounded px-1.5 text-[10px] font-semibold leading-none transition ${
            value === option.value
              ? 'bg-white text-slate-900 shadow-sm'
              : 'text-slate-500 hover:text-slate-800'
          }`}
        >
          {option.label}
        </button>
      ))}
    </div>
  );
}

export function ChartFrameToggle({
  visible,
  value,
  onChange,
}: {
  visible: boolean;
  value: DashboardGranularity;
  onChange: (value: DashboardGranularity) => void;
}) {
  if (!visible) return null;
  return (
    <SegmentedControl
      value={value}
      options={[
        { value: 'day', label: 'День' },
        { value: 'hour', label: 'Час' },
      ]}
      onChange={onChange}
    />
  );
}
